import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useTasks } from '../../context/TasksContext';
import { useLeads } from '../../context/LeadsContext';
import { useRole } from '../../context/RoleContext';
import { useAuth } from '../../context/AuthContext';
import UnscheduledStagingColumn from './UnscheduledStagingColumn';
import EventCard from './WeekScheduler_EventCard';
import WeekSlot from './WeekScheduler_Slot';
import ScheduleJobModal from '../scheduler/ScheduleJobModal';
import { SLOT_HEIGHT, SLOTS_PER_DAY } from './calendarConstants';
import { isBoardVisibleTask } from '../../utils/buildForecast';

const timeSlots = Array.from({ length: SLOTS_PER_DAY }, (_, idx) => idx);

const formatTimeLabel = (idx) => {
  const hour24 = Math.floor(idx / 2);
  const minutes = idx % 2 === 0 ? 0 : 30;
  let hours = hour24 % 12;
  if (hours === 0) hours = 12;
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  const mm = minutes === 0 ? '00' : '30';
  return `${hours}:${mm} ${ampm}`;
};

// ── Collision detection ────────────────────────────────────────────────────────
/**
 * For a list of jobs in one day column, detect time overlaps and return a
 * Map<taskId, CSSStyleObject> so overlapping cards share horizontal space
 * instead of stacking invisibly behind each other.
 *
 * Algorithm: for each job, find ALL concurrently running jobs and assign
 * an equal share of the column width based on group size + position index.
 * O(n²) — fine for the ~5–20 events per day that a typical field-service
 * business schedules.
 */
function computeEventLayout(jobs) {
  if (!jobs.length) return new Map();

  const DEFAULT_DURATION_MS = 60 * 60 * 1000; // 1 hr

  // Sort by start time then ID for a stable, deterministic column order
  const sorted = [...jobs].sort((a, b) => {
    const diff = new Date(a.scheduledStart).getTime() - new Date(b.scheduledStart).getTime();
    return diff || a.id.localeCompare(b.id);
  });

  const result = new Map();

  for (const job of sorted) {
    const startMs  = new Date(job.scheduledStart).getTime();
    const endMs    = job.scheduledEnd ? new Date(job.scheduledEnd).getTime() : startMs + DEFAULT_DURATION_MS;

    // All jobs (including self) whose time windows intersect this one
    const group = sorted.filter((other) => {
      const oStart = new Date(other.scheduledStart).getTime();
      const oEnd   = other.scheduledEnd ? new Date(other.scheduledEnd).getTime() : oStart + DEFAULT_DURATION_MS;
      return startMs < oEnd && endMs > oStart;
    });

    const groupSize = group.length;
    const colIdx    = group.findIndex((g) => g.id === job.id);

    // Pixel-precise top + height
    const start     = new Date(job.scheduledStart);
    const startMins = start.getHours() * 60 + start.getMinutes();
    const endMins   = job.scheduledEnd
      ? new Date(job.scheduledEnd).getHours() * 60 + new Date(job.scheduledEnd).getMinutes()
      : startMins + 60;
    const top    = (startMins / 30) * SLOT_HEIGHT;
    const height = Math.max(1, (endMins - startMins) / 30) * SLOT_HEIGHT;

    const GAP  = 2; // px gutter between side-by-side cards
    const pct  = 100 / groupSize;
    const left = colIdx * pct;

    result.set(job.id, {
      top:    `${top}px`,
      height: `${height}px`,
      left:   `calc(${left}% + ${GAP}px)`,
      right:  'auto',
      width:  `calc(${pct}% - ${GAP * 2}px)`,
    });
  }

  return result;
}

const WeekSchedulerView = ({ weekStart, techFilter, hideUnscheduled = false }) => {
  const { tasks } = useTasks();
  const { leads } = useLeads();
  const { isTechnician } = useRole();
  const { user } = useAuth();

  const jobsFiltered = useMemo(() => {
    // Hide tasks the operator/cascade explicitly killed (cancelled / no-show).
    let jobs = (tasks || []).filter(
      (task) => task.type === 'job' && task.scheduledStart && isBoardVisibleTask(task),
    );
    // Role gate: technicians only see their own jobs
    if (isTechnician && user?.uid) {
      jobs = jobs.filter((task) => task.assignedTo === user.uid);
    }
    // Tech filter pill from SchedulerShell
    if (techFilter === 'unassigned') {
      jobs = jobs.filter((task) => !task.assignedTo);
    } else if (techFilter) {
      jobs = jobs.filter((task) => task.assignedTo === techFilter);
    }
    return jobs;
  }, [tasks, isTechnician, user?.uid, techFilter]);
  const jobsValidDate = useMemo(
    () =>
      jobsFiltered.filter((task) => {
        const d = new Date(task.scheduledStart);
        return !Number.isNaN(d.getTime());
      }),
    [jobsFiltered],
  );

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const scrollRef = useRef(null);
  const [now, setNow] = useState(() => new Date());
  const [slotModal, setSlotModal] = useState({ open: false, date: '', startTime: '' });

  // Scroll to current time on initial render (centered in view)
  useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    requestAnimationFrame(() => {
      const n = new Date();
      const minutes = n.getHours() * 60 + n.getMinutes();
      const rawTop = (minutes / 30) * SLOT_HEIGHT;
      el.scrollTop = Math.max(0, rawTop - el.clientHeight / 3);
    });
  }, []);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const nowLineTop = useMemo(() => {
    const minutes = now.getHours() * 60 + now.getMinutes();
    return (minutes / 30) * SLOT_HEIGHT;
  }, [now]);

  const dayBuckets = useMemo(() => {
    const buckets = Array.from({ length: 7 }, (_, offset) => {
      const date = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + offset);
      date.setHours(0, 0, 0, 0);
      return { date, jobs: [] };
    });

    if (!tasks) return buckets;

    const jobsInRange = [];

    jobsValidDate.forEach((task) => {
      const start = new Date(task.scheduledStart);
      const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const anchorDay = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate());
      const diffMs = startDay.getTime() - anchorDay.getTime();
      const dayIndex = Math.round(diffMs / (24 * 60 * 60 * 1000));

      if (dayIndex < 0 || dayIndex >= buckets.length) return;
      buckets[dayIndex].jobs.push(task);
      jobsInRange.push(task);
    });

    if (process.env.NODE_ENV !== 'production') {
      console.debug('[Week Debug] jobsFiltered:', jobsFiltered.length, 'jobsValidDate:', jobsValidDate.length, 'jobsInRange:', jobsInRange.length);
    }

    return buckets;
  }, [jobsValidDate, jobsFiltered.length, weekStart]);

  // Precompute collision-aware layout for every day column
  const dayLayouts = useMemo(
    () => dayBuckets.map((bucket) => computeEventLayout(bucket.jobs)),
    [dayBuckets],
  );

  const handleSlotClick = (date, slotIdx) => {
    const d = date;
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const hour = Math.floor(slotIdx / 2);
    const minute = (slotIdx % 2) * 30;
    const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    setSlotModal({ open: true, date: dateStr, startTime: timeStr });
  };

  return (
    <>
    <div className="flex text-xs gap-4 mt-4 h-[calc(100vh-220px)]">
      {/* Left: Week grid */}
      <div className="flex-1 bg-white border border-gray-200 rounded-md flex">
        <div ref={scrollRef} className="flex-1 flex flex-col overflow-y-auto">
          {/* Header band — sticky so it stays visible when grid scrolls */}
          <div className="flex h-10 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
            {/* left spacer to align with time axis */}
            <div className="w-20" />
            {/* one header cell per day column */}
            <div className="flex-1 flex text-[12px] font-semibold text-gray-800">
              {dayBuckets.map((bucket, idx) => {
                const isColToday = bucket.date.getTime() === today.getTime();
                return (
                  <div
                    key={idx}
                    className="flex-1 flex flex-col items-center justify-center border-l first:border-l-0 border-gray-200 py-1"
                  >
                    <div className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">
                      {bucket.date.toLocaleDateString(undefined, { weekday: 'short' })}
                    </div>
                    <div className={`text-sm font-bold leading-none mt-0.5 w-7 h-7 flex items-center justify-center rounded-full ${
                      isColToday ? 'bg-[var(--cb-orange)] text-white' : 'text-gray-800'
                    }`}>
                      {bucket.date.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Grid body */}
          <div className="flex flex-1">
            {/* Time axis */}
            <div className="w-20 border-r border-gray-200 box-border flex flex-col text-[10px] text-gray-400">
              {timeSlots.map((idx) => (
                <div
                  key={idx}
                  className="box-border border-b border-dashed border-gray-100 flex items-start justify-end pr-1 text-[11px] text-gray-500 bg-white"
                  style={{ height: SLOT_HEIGHT }}
                >
                  <span>{formatTimeLabel(idx)}</span>
                </div>
              ))}
            </div>

            {/* Day columns */}
            <div className="flex-1 flex text-[11px] select-none">
              {dayBuckets.map((bucket, dayIndex) => (
                <div
                  key={dayIndex}
                  className={`flex-1 border-l first:border-l-0 border-gray-200 box-border flex flex-col relative ${
                    bucket.date.getTime() === today.getTime() ? 'bg-[#F8FAFC]' : 'bg-white'
                  }`}
                >
                  {/* Jobs overlay */}
                  <div className="absolute inset-0">
                    {bucket.jobs.map((task) => {
                      const style = dayLayouts[dayIndex]?.get(task.id) ?? {};
                      return (
                        <EventCard key={task.id} task={task} dayIndex={dayIndex} style={style} />
                      );
                    })}
                    {/* Current time indicator — only on today's column */}
                    {bucket.date.getTime() === today.getTime() && (
                      <div
                        style={{
                          position: 'absolute',
                          top: nowLineTop,
                          left: 0,
                          right: 0,
                          height: 2,
                          background: 'var(--cb-orange)',
                          zIndex: 20,
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                  </div>

                  {/* Grid slots */}
                  <div className="flex flex-col">
                    {timeSlots.map((slotIdx) => (
                      <WeekSlot
                        key={slotIdx}
                        date={bucket.date}
                        dayIndex={dayIndex}
                        slotIdx={slotIdx}
                        onSlotClick={handleSlotClick}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right: staging column — hidden for technicians (read-only, assigned-only view) */}
      {!hideUnscheduled && <UnscheduledStagingColumn leads={leads} tasks={tasks} />}
    </div>

    <ScheduleJobModal
      isOpen={slotModal.open}
      onClose={() => setSlotModal(s => ({ ...s, open: false }))}
      initialDate={slotModal.date}
      initialStartTime={slotModal.startTime}
    />
    </>
  );
};

export default WeekSchedulerView;
